import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute, Data, Event, NavigationEnd, Router, RoutesRecognized } from '@angular/router';
import { Observable, ObservableInput, of, ReplaySubject, Subject, Subscription } from 'rxjs';
import { filter, map, mergeMap, pairwise, skip, take, takeUntil } from 'rxjs/operators';
import { Title } from '@angular/platform-browser';

import { TranslateService } from '../translate/translate.service';
import { A11yService } from './a11y.service';
import { BusMessageChannels, BusMessageService } from './bus-message.service';
import { Api } from '../../../shared/constants/api';
import { TranslateArgs } from '../resolvers/feature-flag-translation.resolver';
import { Layouts } from '../constants/layouts.constants';
import { config } from '../../environments/config.shared';
import { SharedCommonUtility } from '../../../shared/utils/common.utility';
import { CommonUtility } from '../utility/common.utility';

@Injectable({ providedIn: 'root' })
export class RoutingService implements OnDestroy {
  private subscriptions: Subscription;
  private routeChanged$: Subject<void>;
  private routeLayout$: ReplaySubject<Layouts>;
  private pageTitleSnapshot: string;
  private pageDescriptionSnapshot: string;

  public readonly pageTitle$: ReplaySubject<string>;
  public readonly pageLayout$: Observable<Layouts>;
  public previousRoutePath: string;
  private moveFocusOnNavigationEnd: boolean = false;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private translateService: TranslateService,
    private a11yService: A11yService,
    private busMessageService: BusMessageService,
    private title: Title,
  ) {
    const changeRouterSubscription: Subscription = this.subscribeToRouterChange();
    const subscribeToTrackRouterPathSubscription: Subscription = this.subscribeToTrackRouterPath();

    this.subscriptions = new Subscription();
    this.subscriptions.add(changeRouterSubscription);
    this.subscriptions.add(subscribeToTrackRouterPathSubscription);

    this.routeChanged$ = new Subject<void>();
    this.routeLayout$ = new ReplaySubject(1);
    this.pageTitle$ = new ReplaySubject<string>(1);
    this.pageLayout$ = this.routeLayout$.asObservable();

    this.subscriptions.add(
      this.pageTitle$.subscribe((pageTitle: string) => {
        this.setBrowserTitle(pageTitle);
      }),
    );

    const firstChild = this.findFirstChild(this.activatedRoute);
    if (firstChild) {
      if (firstChild.data instanceof Observable) {
        this.subscriptions.add(
          firstChild.data.pipe(take(1)).subscribe((data: Data) => {
            this.handleRouteData(data, true);
          }),
        );
      } else {
        this.handleRouteData(firstChild.data, true);
      }
    }
  }

  private isHomePage(pageTitle: string): boolean {
    return typeof pageTitle === 'string' && pageTitle.length > 0 && pageTitle === config.APP.NAME;
  }

  private setBrowserTitle(pageTitle: string): void {
    if (this.isHomePage(pageTitle) || SharedCommonUtility.isNullishOrEmpty(pageTitle)) {
      this.title.setTitle(config.APP.NAME);
    } else {
      this.title.setTitle(pageTitle + ' | ' + config.APP.NAME);
    }
  }

  private notifyScreenReader(pageTitle: string): void {
    const message = this.translateService.instant('new_page_loaded', pageTitle);
    this.a11yService.setMessage(message);
  }

  private focusMainContent(): void {
    CommonUtility.setFocusToElement('main-content');
  }

  public requestMoveFocusOnNavigationEnd(): void {
    this.moveFocusOnNavigationEnd = true;
  }

  private handleRouteData = (
    routeData: Data,
    isInitialRoute: boolean = false,
    moveFocusOnNavigationEnd: boolean = false,
  ): void => {
    this.routeChanged$.next();

    this.subscriptions.add(
      this.resolvePageTitle(routeData.title)
        .pipe(takeUntil(this.routeChanged$))
        .subscribe((pageTitle: string) => {
          this.pageTitleSnapshot = pageTitle;
          this.pageTitle$.next(pageTitle);
          // Only notify screen reader for non-initial routes, otherwise the router's own NavigationEnd for the initial
          // navigation would trigger a second, duplicate "new page loaded" announcement right after this one
          if (!isInitialRoute) {
            this.notifyScreenReader(pageTitle);
          }
          if (moveFocusOnNavigationEnd) {
            this.focusMainContent();
          }
        }),
    );

    this.subscriptions.add(
      this.resolvePageDescription(routeData.description)
        .pipe(takeUntil(this.routeChanged$))
        .subscribe((pageDescription: string) => {
          this.pageDescriptionSnapshot = pageDescription;
        }),
    );

    this.routeLayout$.next(routeData.layout);
  };

  private findFirstChild(route: ActivatedRoute): ActivatedRoute {
    let _route: ActivatedRoute = route;

    while (_route.firstChild) {
      _route = _route.firstChild;
    }

    return _route;
  }

  private subscribeToRouterChange(): Subscription {
    const filterRoutes = (event: Event): event is NavigationEnd => {
      return event instanceof NavigationEnd;
    };

    const getActivatedRoute: any = (): ActivatedRoute => {
      return this.activatedRoute;
    };

    const getOnlyPrimary = (route: ActivatedRoute): boolean => {
      return route.outlet === 'primary';
    };

    const getRouteData = (route: ActivatedRoute): ObservableInput<Data> => {
      return route.data;
    };

    return this.router.events
      .pipe(
        filter(filterRoutes),
        map(getActivatedRoute),
        map(this.findFirstChild),
        filter(getOnlyPrimary),
        mergeMap(getRouteData),
      )
      .subscribe((data: Data) => {
        const moveFocusOnNavigationEnd: boolean = this.moveFocusOnNavigationEnd;
        this.moveFocusOnNavigationEnd = false;
        this.handleRouteData(data, false, moveFocusOnNavigationEnd);
      });
  }

  private subscribeToTrackRouterPath(): Subscription {
    return this.router.events
      .pipe(
        filter((e: Event): e is RoutesRecognized => {
          return e instanceof RoutesRecognized;
        }),
        pairwise(),
      )
      .subscribe((event: [RoutesRecognized, RoutesRecognized]) => {
        this.previousRoutePath = (event[0] as RoutesRecognized).urlAfterRedirects;
      });
  }

  public resolvePageTitle(args: TranslateArgs): Observable<string> {
    const translationArgs = Array.isArray(args) ? args : [args];
    return of(this.translateService.instant.apply(this.translateService, translationArgs));
  }

  public resolvePageDescription(args: TranslateArgs): Observable<string> {
    const translationArgs = Array.isArray(args) ? args : [args];
    return of(this.translateService.instant.apply(this.translateService, translationArgs));
  }

  public getPageTitle(): string {
    return this.pageTitleSnapshot ?? '';
  }

  public getPageDescription(): string {
    return this.pageDescriptionSnapshot ?? '';
  }

  public navigateOnPropertyChange(route: string[] = ['/', Api.dashboard]): Subscription {
    return this.busMessageService
      .from(BusMessageChannels.userSwitchedToDigitalProperty)
      .pipe(
        skip(1),
        mergeMap(async () => this.router.navigate(route)),
      )
      .subscribe();
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
